import { useState } from 'react';

import { Button, Toast, useToast } from '@zillow/constellation-10';
import { IconDownloadFilled } from '@zillow/constellation-icons';

import { getBooleanConfig } from '@zg-rentals/frontend-config/client';
import { logError } from '@zg-rentals/log-error';
import { ExportLandlordTransactionsWebApiHandler_exportLandlordTransactions } from '@zg-rentals/ts-rent-payment-client';

import { downloadCsv } from './downloadCsv';

type Props = {
  propertyId?: string;
};

const buildAllTimeRange = () => {
  const today = new Date();
  return {
    startDate: { year: 2000, month: 1, day: 1 },
    endDate: { year: today.getFullYear(), month: today.getMonth() + 1, day: today.getDate() },
  };
};

const TransactionDownloadButtonInner = ({ propertyId }: Props) => {
  const { enqueueToast } = useToast();
  const [isDownloading, setIsDownloading] = useState(false);

  const handleClick = async () => {
    if (!propertyId) {
      return;
    }

    setIsDownloading(true);

    try {
      const { startDate, endDate } = buildAllTimeRange();

      const response = (await ExportLandlordTransactionsWebApiHandler_exportLandlordTransactions({
        propertyIds: [propertyId],
        startDate,
        endDate,
        paymentTypes: [],
        paymentStatuses: [],
        filterUnpaidPayments: false,
      })) as unknown as Response;

      if (!response.ok) {
        throw new Error(`Export request failed with status ${response.status}`);
      }

      const csv = await response.text();
      const datePart = `${endDate.year}-${String(endDate.month).padStart(2, '0')}-${String(endDate.day).padStart(2, '0')}`;
      downloadCsv(csv, `transactions_${datePart}.csv`);

      enqueueToast(<Toast tone="success">Transactions downloaded</Toast>);
    } catch (error) {
      logError({ error, context: 'TransactionDownloadButton' });
      enqueueToast(<Toast tone="critical">Transactions couldn’t be downloaded. Wait and try again.</Toast>);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Button
      size="sm"
      icon={<IconDownloadFilled />}
      iconPosition="start"
      onClick={handleClick}
      loading={isDownloading}
      disabled={isDownloading}
    >
      Download
    </Button>
  );
};

const TransactionDownloadButton = ({ propertyId }: Props) => {
  if (!getBooleanConfig('isLandlordMarkAsPaidOfflineEnabled', false)) {
    return null;
  }

  return <TransactionDownloadButtonInner propertyId={propertyId} />;
};

export default TransactionDownloadButton;
